import { useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCondemnations } from "@/features/condemnation/api";
import { CONDEMNATION_STATUSES } from "@/features/condemnation/types";
import { CondemnationStatusBadge } from "@/features/condemnation/badges";
import { useOptionValues } from "@/features/configuration/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked } from "@/lib/roles";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/condemnation/")({
  component: CondemnationIndex,
});

const WRITER = new Set(["super_admin", "biomed_head", "facility_manager", "finance", "management"]);

function CondemnationIndex() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const { values: statusOpts } = useOptionValues("condemnation", "review_status");
  const statusChoices = statusOpts.length ? statusOpts.map((o) => o.value) : [...CONDEMNATION_STATUSES];
  const { data: rows = [], isLoading } = useCondemnations({
    status: status === "all" ? null : status,
    search,
    branchId: effectiveBranchId,
  });
  const canCreate = (me?.roles ?? []).some((r) => WRITER.has(r));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Condemnation Workflow</h1> <ReadOnlyPill module="condemnation" />
          <p className="text-sm text-muted-foreground">Biomedical / Finance / Management committee approval.</p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate({ to: "/condemnation/new" })}><Plus className="mr-1 h-4 w-4" />New Request</Button>
        )}
      </div>

      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-56" placeholder="Search request / asset…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusChoices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No requests found.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Request No</TableHead><TableHead>Asset</TableHead>
              <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/condemnation/$id", params: { id: r.id } })}>
                  <TableCell className="font-mono text-xs">
                    <Link to="/condemnation/$id" params={{ id: r.id }} className="text-primary hover:underline">{r.request_no}</Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-mono text-xs">{r.asset?.asset_code}</div>
                    <div className="text-xs text-muted-foreground">{r.asset?.name}</div>
                  </TableCell>
                  <TableCell className="text-sm">{r.reason}</TableCell>
                  <TableCell><CondemnationStatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
